/**
 * ListingsMap component using Mapbox GL
 *
 * Desktop: hover to show popup (no close button)
 * Mobile: click marker to show popup with close button
 */

import { useEffect, useRef, useCallback, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { Listing } from "@/shared/types/listings";
import { getLocalized } from "@/shared/types/primitives";
import { useTranslation, useCurrentLocale } from "@/shared/hooks";
import { MapPin, AlertTriangle } from "lucide-react";
import { env } from "@/shared/config/env";

const MAPBOX_TOKEN = env.mapboxToken;

const DEFAULT_CENTER: [number, number] = [139.6917, 35.6895];
const DEFAULT_ZOOM = 11;
const MARKER_COLOR = "#ef4444";

interface ListingsMapProps {
  listings: Listing[];
  selectedListing?: Listing | null;
  onListingSelect?: (listing: Listing) => void;
  onListingHover?: (listing: Listing | null) => void;
  onListingClick?: (listing: Listing) => void;
  /** Whether to use mobile interaction mode (click to toggle popup) */
  isMobile?: boolean;
  className?: string;
}

function formatPrice(amount: number): string {
  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency: "JPY",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function ListingsMap({
  listings,
  selectedListing,
  onListingHover,
  onListingClick,
  isMobile = false,
  className = "",
}: ListingsMapProps) {
  const t = useTranslation();
  const locale = useCurrentLocale();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [activeListingId, setActiveListingId] = useState<string | null>(null);
  const hasFitBoundsRef = useRef(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    try {
      mapboxgl.accessToken = MAPBOX_TOKEN;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        attributionControl: false,
      });

      // Only add navigation control
      map.addControl(new mapboxgl.NavigationControl(), "top-right");

      map.on("load", () => {
        setIsMapReady(true);
      });

      map.on("error", (e) => {
        console.error("Mapbox error:", e);
        setMapError(
          "Failed to load map. Please check your internet connection.",
        );
      });

      // Close popup when clicking on empty map area (mobile only)
      map.on("click", (e) => {
        const target = e.originalEvent.target as HTMLElement;
        if (target.closest(".mapboxgl-marker")) return;
        if (isMobile && popupRef.current) {
          popupRef.current.remove();
          setActiveListingId(null);
        }
      });

      mapRef.current = map;

      // Create single popup instance
      popupRef.current = new mapboxgl.Popup({
        closeButton: isMobile,
        closeOnClick: false,
        maxWidth: "240px",
        className: isMobile
          ? "listing-map-popup mobile"
          : "listing-map-popup desktop",
      });

      popupRef.current.on("close", () => {
        setActiveListingId(null);
      });

      return () => {
        map.remove();
        mapRef.current = null;
      };
    } catch (error) {
      console.error("Error initializing map:", error);
      setMapError("Failed to initialize map.");
    }
  }, [isMobile]);

  // Create marker element
  const createMarkerElement = useCallback(
    (_listing: Listing, isSelected: boolean) => {
      const el = document.createElement("div");
      el.style.cursor = "pointer";

      const scale = isSelected ? 1.25 : 1;
      const boxShadow = isSelected
        ? `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 0 0 2px ${MARKER_COLOR}40`
        : "0 4px 6px -1px rgba(0, 0, 0, 0.1)";

      el.innerHTML = `
        <div style="position: relative; transform: scale(${scale}); transition: transform 0.15s ease;">
          <div style="
            width: 28px;
            height: 28px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${MARKER_COLOR};
            border: 2px solid white;
            box-shadow: ${boxShadow};
          ">
            <span style="color: white; font-size: 12px; font-weight: bold;">¥</span>
          </div>
        </div>
      `;

      // Add hover effect for non-selected markers
      if (!isSelected) {
        el.addEventListener("mouseenter", () => {
          const inner = el.firstElementChild as HTMLElement;
          if (inner) inner.style.transform = "scale(1.1)";
        });
        el.addEventListener("mouseleave", () => {
          const inner = el.firstElementChild as HTMLElement;
          if (inner) inner.style.transform = "scale(1)";
        });
      }

      return el;
    },
    [],
  );

  // Generate compact popup HTML
  const getPopupHTML = useCallback(
    (listing: Listing) => {
      // Get primary image
      const allImages = Object.values(listing.media.images_by_section).flat();
      const primaryImage =
        allImages.find((img) => img.is_featured) || allImages[0];
      const imageUrl =
        primaryImage?.thumbnail_url ||
        "https://via.placeholder.com/240x160/e5e7eb/9ca3af?text=No+Image";

      const ward = listing.location.ward
        ? getLocalized(listing.location.ward, locale)
        : "";
      const city = getLocalized(listing.location.city, locale);

      const locationParts = [ward, city].filter(Boolean);
      const locationString = locationParts.join(", ") || "Tokyo";

      const title = getLocalized(listing.title, locale);
      const price = listing.financials.rent.amount;
      const roomLayout = listing.property.room_layout;
      const area = listing.property.area_sqm;

      return `
      <article class="bg-white rounded-lg overflow-hidden cursor-pointer w-[220px]" data-listing-id="${listing.id}">
        <div class="relative aspect-[3/2] overflow-hidden bg-gray-100">
          <img
            src="${imageUrl}"
            alt="${title || roomLayout || "Apartment"}"
            class="w-full h-full object-cover"
            onerror="this.src='https://via.placeholder.com/240x160/e5e7eb/9ca3af?text=No+Image'"
          />
          <div class="absolute top-1.5 right-1.5 bg-black/70 text-white px-1.5 py-0.5 rounded text-xs font-medium">
            ${roomLayout || "N/A"}
          </div>
        </div>
        <div class="p-2">
          <div class="mb-1">
            <span class="text-base font-bold text-gray-900">${formatPrice(price)}</span>
            <span class="text-[10px] text-gray-500">/${t("listings.month")}</span>
          </div>
          <div class="flex items-center gap-1 text-gray-600 text-xs mb-1">
            <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span class="line-clamp-1">${locationString}</span>
          </div>
          <div class="flex items-center gap-1 text-xs text-gray-500">
            <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
            </svg>
            <span>${area || "?"} m²</span>
          </div>
        </div>
      </article>
    `;
    },
    [t, locale],
  );

  // Show popup
  const showPopup = useCallback(
    (listing: Listing, coords: [number, number]) => {
      if (!mapRef.current || !popupRef.current) return;

      const popupContent = getPopupHTML(listing);
      popupRef.current
        .setLngLat(coords)
        .setHTML(popupContent)
        .addTo(mapRef.current);
      setActiveListingId(listing.id);

      // Add click handler
      setTimeout(() => {
        const popupEl = popupRef.current?.getElement();
        if (popupEl) {
          const cardEl = popupEl.querySelector("[data-listing-id]");
          if (cardEl) {
            cardEl.addEventListener("click", () => {
              onListingClick?.(listing);
            });
          }
        }
      }, 0);
    },
    [getPopupHTML, onListingClick],
  );

  // Hide popup
  const hidePopup = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.remove();
      setActiveListingId(null);
    }
  }, []);

  // Update markers when listings change
  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;

    const map = mapRef.current;
    const currentMarkers = markersRef.current;

    const currentIds = new Set(listings.map((l) => l.id));
    currentMarkers.forEach((marker, id) => {
      if (!currentIds.has(id)) {
        marker.remove();
        currentMarkers.delete(id);
      }
    });

    const bounds = new mapboxgl.LngLatBounds();
    let hasValidCoords = false;

    listings.forEach((listing) => {
      if (
        !listing.location.coordinates?.lat ||
        !listing.location.coordinates?.lng
      )
        return;

      hasValidCoords = true;
      const coords: [number, number] = [
        listing.location.coordinates.lng,
        listing.location.coordinates.lat,
      ];
      bounds.extend(coords);

      const isSelected = selectedListing?.id === listing.id;
      let marker = currentMarkers.get(listing.id);

      if (marker) {
        const el = marker.getElement();
        el.replaceChildren();
        const newEl = createMarkerElement(listing, isSelected);
        el.appendChild(newEl.firstElementChild!);
      } else {
        const el = createMarkerElement(listing, isSelected);

        marker = new mapboxgl.Marker({
          element: el,
          anchor: "bottom",
        })
          .setLngLat(coords)
          .addTo(map);

        if (isMobile) {
          el.addEventListener("click", (e) => {
            e.stopPropagation();
            if (activeListingId === listing.id) {
              hidePopup();
            } else {
              showPopup(listing, coords);
            }
            onListingHover?.(listing);
          });
        } else {
          el.addEventListener("mouseenter", () => {
            onListingHover?.(listing);
            showPopup(listing, coords);
          });

          el.addEventListener("mouseleave", () => {
            onListingHover?.(null);
            hidePopup();
          });

          el.addEventListener("click", () => {
            onListingClick?.(listing);
          });
        }

        currentMarkers.set(listing.id, marker);
      }
    });

    // Only fit bounds on initial load
    if (hasValidCoords && listings.length > 0 && !hasFitBoundsRef.current) {
      map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
      });
      hasFitBoundsRef.current = true;
    }
  }, [
    listings,
    selectedListing,
    isMapReady,
    isMobile,
    createMarkerElement,
    showPopup,
    hidePopup,
    activeListingId,
    onListingHover,
    onListingClick,
  ]);

  // Handle selected listing change (from list view hover)
  useEffect(() => {
    if (
      !mapRef.current ||
      !selectedListing?.location.coordinates?.lat ||
      !selectedListing?.location.coordinates?.lng
    )
      return;

    const coords: [number, number] = [
      selectedListing.location.coordinates.lng,
      selectedListing.location.coordinates.lat,
    ];

    showPopup(selectedListing, coords);
  }, [selectedListing, showPopup]);

  if (mapError) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 ${className}`}
      >
        <div className="text-center p-8">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("overviewMapComingSoon")}
          </h3>
          <p className="text-gray-500 text-sm max-w-xs mx-auto">{mapError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={mapContainerRef} className="w-full h-full min-h-[400px]" />

      {!isMapReady && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-primary-500 animate-pulse mx-auto mb-4" />
            <p className="text-gray-600">Loading map...</p>
          </div>
        </div>
      )}

      {isMapReady && listings.length === 0 && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white/95 rounded-lg shadow-lg px-4 py-2 text-sm text-gray-600 z-10">
          {t("overviewEmptyTitle")}
        </div>
      )}

      <style>{`
        .mapboxgl-ctrl-logo,
        .mapboxgl-ctrl-attrib {
          display: none !important;
        }

        .listing-map-popup .mapboxgl-popup-content {
          padding: 0 !important;
          border-radius: 0.5rem !important;
          box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.15) !important;
        }

        /* Desktop: hide close button */
        .listing-map-popup.desktop .mapboxgl-popup-close-button {
          display: none !important;
        }

        /* Mobile: styled close button */
        .listing-map-popup.mobile .mapboxgl-popup-close-button {
          position: absolute;
          top: -8px;
          right: -8px;
          z-index: 30;
          width: 24px;
          height: 24px;
          background: #ef4444;
          color: white;
          border-radius: 50%;
          font-size: 16px;
          font-weight: 400;
          font-family: Arial, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
          padding: 0;
          margin: 0;
          line-height: 1;
          text-align: center;
        }

        .listing-map-popup.mobile .mapboxgl-popup-close-button:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .listing-map-popup.mobile .mapboxgl-popup-content {
          overflow: visible !important;
        }

        .listing-map-popup .mapboxgl-popup-tip {
          border-top-color: white !important;
        }

        .listing-map-popup article {
          border-radius: 0.5rem;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default ListingsMap;
